import humps from 'humps';

import type {
  Media,
  MediaDetail,
  MediaDimensions,
  MediaPlatformData,
  MediaSize,
  MediaSizes,
} from '@/utils/media.types';

import { http } from '@/apis/library';
import enumTypes, {
  PLATFORM_MEDIA_TYPES,
  PLATFORM_MEDIA_TYPES_MAP,
  SNAPCHAT_MEDIA_TYPES,
} from '@/app/library/constants';
import { constants, SOURCE_TO_METRIC_PROP, typeEnum, UPLOAD_STATUS } from '@/config';
import { formatTime } from '@/utils/formatters';

const MAX_CROPPER_IMAGE_DIMENSION = 11000 * 11000;

// Dynamic, string-keyed lookups into these JS constant maps. Casting to a string-indexed
// record keeps the dynamic access type-safe without re-typing the source JS modules.
const sourceToMetricProp = SOURCE_TO_METRIC_PROP as Record<string, string | undefined>;
const platformMediaTypesMap = PLATFORM_MEDIA_TYPES_MAP as Record<string, string | undefined>;
const snapchatMediaTypes = SNAPCHAT_MEDIA_TYPES as Record<string, string | undefined>;

// Reads a dynamic (computed) key off a media-like object. The keys here are channel/metric
// names resolved at runtime, so they cannot be expressed as `keyof` of our static types.
function readDynamicKey(obj: unknown, key: string): unknown {
  if (obj && typeof obj === 'object') {
    return (obj as Record<string, unknown>)[key];
  }
  return undefined;
}

export function getMediaTypeFromDetail(
  mediaDetail: MediaDetail | null | undefined,
): 'video' | 'photo' | 'media' {
  switch (mediaDetail?.mediaType ?? '') {
    case 'VIDEO':
      return 'video';
    case 'IMAGE':
      return 'photo';
    default:
      return 'media';
  }
}

// Following two functions are identical should remove
export function getImageDataFromMedia(media: Media): MediaSize | null | undefined {
  // Uploaded to scheduler popup (MediaV2)
  if (media.fullMediaObject) {
    return media.fullMediaObject.sizes?.originalConverted || media.fullMediaObject.sizes?.original;
  }
  // everything else MediaV2
  return media.sizes?.originalConverted || media.sizes?.original;
}

export function getVideoDataFromMedia(
  media: Media | null | undefined,
): MediaSize | null | undefined {
  // Uploaded to scheduler popup (MediaV2)
  if (media?.fullMediaObject) {
    return media.fullMediaObject.sizes?.originalConverted || media.fullMediaObject.sizes?.original;
  }
  // everything else MediaV2
  return media?.sizes?.originalConverted || media?.sizes?.original || null;
}

/**
 * Extracts width and height dimensions from a media object.
 * Checks multiple possible sources: sizes, imageSizes, videoSizes, fullMediaObject.
 * Prefers originalConverted, falls back to original, then direct width/height.
 * @param media - Media object with size information
 * @returns Dimensions or null if not found
 */
export function getMediaDimensions(media: Media | null | undefined): MediaDimensions | null {
  if (!media) return null;

  const sources: Array<MediaSize | null | undefined> = [
    media.fullMediaObject?.sizes?.originalConverted,
    media.fullMediaObject?.sizes?.original,
    media.fullMediaObject?.imageSizes?.originalConverted,
    media.fullMediaObject?.imageSizes?.original,
    media.fullMediaObject?.videoSizes?.originalConverted,
    media.fullMediaObject?.videoSizes?.original,
    media.sizes?.originalConverted,
    media.sizes?.original,
    media.imageSizes?.originalConverted,
    media.imageSizes?.original,
    media.videoSizes?.originalConverted,
    media.videoSizes?.original,
  ];

  for (const source of sources) {
    if (source?.width && source?.height) {
      return { width: source.width, height: source.height };
    }
  }

  return media.width && media.height ? { width: media.width, height: media.height } : null;
}

export function mediaIsUploading(media: Media | null | undefined): boolean {
  return (
    Boolean(media?.uploadStatus) &&
    media?.uploadStatus !== UPLOAD_STATUS.SUCCESS &&
    media?.uploadStatus !== UPLOAD_STATUS.PROCESSING
  );
}

export async function getVideoDuration(
  media: Media,
  brandId: number | string,
): Promise<number | null | undefined> {
  let videoDuration: number | null | undefined = null;
  // Only videos selected from the global popup will have a value for duration.
  // Otherwise we need to call library to get that value.
  if (media.duration === undefined || media.duration == null) {
    const res = await http.get<{ duration?: number }>(`/brands/${brandId}/media/${media.id}`);
    videoDuration = res.data.duration;
  } else {
    videoDuration = media.duration;
  }
  return videoDuration;
}

export function mediaSizeIsCroppable(media: Media): boolean {
  if (media.type === 'IMAGE') {
    const mediaObject = media.fullMediaObject || media;
    const imageWidth = mediaObject.sizes?.originalConverted
      ? mediaObject.sizes.originalConverted.width
      : mediaObject.sizes?.original?.width;
    const imageHeight = mediaObject.sizes?.originalConverted
      ? mediaObject.sizes.originalConverted.height
      : mediaObject.sizes?.original?.height;

    // Larger images could cause cropper crashes
    return Number(imageWidth) * Number(imageHeight) <= MAX_CROPPER_IMAGE_DIMENSION;
  }
  // TODO: if future media size validation is decided upon, it can exist here. For now return
  // true if the media type is video.
  return media.type === 'VIDEO';
}

export function getMediaSourceKey(media: Media | null | undefined): string | undefined {
  return sourceToMetricProp[media?.source ?? ''] || media?.source?.toLowerCase();
}

function getMediaSourceData(media: Media | null | undefined): MediaPlatformData | undefined {
  const sourceKey = getMediaSourceKey(media);
  return readDynamicKey(media, sourceKey ?? '') as MediaPlatformData | undefined;
}

export function isMediaVideo(media: Media | null | undefined): boolean {
  const isVideoMedia = media?.type === 'VIDEO' || media?.mediaType === 'VIDEO';
  const isInstagramReelVideo = media?.type?.toUpperCase() === 'REEL';
  const isPinterestVideo = media?.pinterest?.isVideo;
  const isTikTokVideo = media?.tiktok && media.tiktok.mediaType?.toUpperCase() !== 'PHOTO';
  const isYouTubeVideo = media?.youtube;
  const isFacebookVideo = media?.facebook?.type?.toUpperCase() === 'VIDEO';
  const isSnapchatVideo =
    (media?.mediaType?.toUpperCase() === 'SNAP' ||
      media?.mediaType?.toUpperCase() === 'SPOTLIGHT') &&
    media?.media_type?.toUpperCase() === 'VIDEO';
  return Boolean(
    isVideoMedia ||
      isInstagramReelVideo ||
      isPinterestVideo ||
      isTikTokVideo ||
      isYouTubeVideo ||
      isFacebookVideo ||
      isSnapchatVideo,
  );
}

export function isMediaText(media: Media | null | undefined): boolean {
  const isFacebookText = media?.facebook?.type === 'status';
  const isLinkedInText = media?.linkedin?.type === 'TEXT';
  const isThreadsText = media?.threads?.type === 'TEXT_POST';
  const isTwitterText = media?.twitter?.type === 'text';
  return isFacebookText || isLinkedInText || isThreadsText || isTwitterText;
}

export function isMediaDocument(media: Media | null | undefined): boolean {
  const isLinkedInDocument = media?.linkedin?.type === 'DOCUMENT';
  return isLinkedInDocument;
}

export function isMediaAudio(media: Media | null | undefined): boolean {
  const isThreadsAudio = media?.threads?.type === 'AUDIO';
  return isThreadsAudio;
}

export function isMediaLink(media: Media | null | undefined): boolean {
  return (
    enumTypes.FACEBOOK_LINK_LIST.includes(media?.source ?? '') ||
    enumTypes.TWITTER_LINK_LIST.includes(media?.source ?? '')
  );
}

/**
 * Checks if media is an exportable image (excludes videos, carousels, text, documents, audio, links)
 */
export function isMediaImage(media: Media | null | undefined): boolean {
  const mediaType = media?.type || media?.mediaType || media?.media_type;

  if (
    isMediaVideo(media) ||
    isMediaText(media) ||
    isMediaDocument(media) ||
    isMediaAudio(media) ||
    isMediaLink(media)
  ) {
    return false;
  }

  if (media?.tiktok && media.tiktok.mediaType?.toUpperCase() === 'PHOTO') {
    return false;
  }

  if (mediaType?.toUpperCase() === 'CAROUSEL') {
    return false;
  }

  if (mediaType?.toUpperCase() === 'IMAGE' && !media?.is_video && !media?.isVideo) {
    return true;
  }

  if (
    (mediaType?.toUpperCase() === 'SNAP' || mediaType?.toUpperCase() === 'SPOTLIGHT') &&
    media?.media_type?.toUpperCase() === 'IMAGE'
  ) {
    return true;
  }

  return false;
}

export function isMediaReel(media: Media | null | undefined): boolean {
  return media?.instagram?.postType?.toLowerCase() === 'reel';
}

export function isMediaPromoted(media: Media): boolean {
  return Boolean(
    media.instagram?.isBoosted ||
      media.facebook?.isBoosted ||
      media.twitter?.isPromoted ||
      media.instagram?.is_boosted ||
      media.facebook?.is_boosted ||
      media.twitter?.is_promoted ||
      media.tiktok?.distributionType === 'PROMOTED',
  );
}

export function isMediaCarousel(media: Media | null | undefined): boolean {
  if (media?.instagram) {
    return Boolean(media?.instagram?.isCarousel);
  }

  const sourceData = getMediaSourceData(media);
  if (sourceData?.twitter?.additionalMediaUrls) {
    return sourceData.twitter.additionalMediaUrls !== '';
  }
  return sourceData?.type?.toLowerCase() === 'carousel';
}

export function getMediaTypeLabel(media: Media | null | undefined): 'Video' | 'Carousel' | 'Image' {
  if (isMediaVideo(media)) return 'Video';
  if (isMediaCarousel(media)) return 'Carousel';
  return 'Image';
}

function getMediaImageUrl(media: Media | null | undefined): string | undefined {
  const isVideo = isMediaVideo(media);
  const isPinterestVideo = media?.pinterest?.isVideo;
  let mediaImages: MediaSizes | undefined;

  if (isPinterestVideo) {
    mediaImages = media?.image?.sizes;
  } else {
    mediaImages = isVideo ? media?.video?.thumbnails : media?.image?.sizes;
  }
  return humps.camelizeKeys(mediaImages)?.smallSquare?.url;
}

function getMediaPostUrl(media: Media | null | undefined): string | undefined {
  const source = media?.source;
  const sourceData = media?.sourceData;
  if (source === 'PINTEREST') {
    return sourceData?.link;
  }
  if (source === 'TWITTER') {
    return sourceData?.permalinkUrl;
  }
  if (source === 'TIKTOK') {
    return sourceData?.shareUrl;
  }
  return sourceData?.url;
}

export function getMediaVideoDuration(media: Media | null | undefined): number | undefined {
  return (
    media?.video?.duration ||
    media?.tiktok?.duration ||
    media?.youtube?.duration ||
    media?.videoDuration ||
    undefined
  );
}

export function resolvePlatformMediaType(media: Media | null | undefined): string | null {
  const camelMedia = humps.camelizeKeys(media ?? {}) as Media;
  const channel = sourceToMetricProp[media?.source ?? ''] || media?.source?.toLowerCase();

  if (!channel) return null;

  const channelData =
    (readDynamicKey(camelMedia, humps.camelize(channel)) as MediaPlatformData | undefined) ?? {};
  const data = { ...camelMedia, ...channelData } as Media & MediaPlatformData;

  if (data.source?.includes('_LINK')) {
    return PLATFORM_MEDIA_TYPES.LINK;
  }

  const type = (data.mediaType || data.postType || data.type)?.toUpperCase() ?? null;

  if (channel === constants.TIKTOK.toLowerCase()) {
    if (data?.tiktok?.mediaType === 'PHOTO') {
      return PLATFORM_MEDIA_TYPES.IMAGE;
    }
    return PLATFORM_MEDIA_TYPES.VIDEO;
  }

  const channelData2 = readDynamicKey(data, channel) as MediaPlatformData | undefined;
  if (
    type === typeEnum.MULTI_IMAGE ||
    (channelData2?.additionalMediaUrls?.length ?? 0) > 0 ||
    (channelData2?.carouselMediaList?.length ?? 0) > 0
  ) {
    return PLATFORM_MEDIA_TYPES.CAROUSEL;
  }

  if (channel === constants.INSTAGRAM_STORY_FRAME.toLowerCase()) {
    return type === 'VIDEO' ? PLATFORM_MEDIA_TYPES.STORY_FRAME : PLATFORM_MEDIA_TYPES.STORY;
  }

  if (channel === constants.THREADS.toLowerCase() && data.threads?.type === 'TEXT_POST') {
    return PLATFORM_MEDIA_TYPES.TEXT;
  }

  if (channel === constants.SNAPCHAT.toLowerCase()) {
    return snapchatMediaTypes?.[data.snapchat?.assetType ?? ''] ?? type;
  }

  return platformMediaTypesMap[type ?? ''] || type;
}

export function formatMediaVideoDuration(media: Media | null | undefined): string {
  // default duration string placeholder is '--:--'
  let durationString = '--:--';
  const isPinterestVideo = media?.pinterest?.isVideo ?? media?.pinterest?.is_video;
  const videoDuration = getMediaVideoDuration(media);
  if (isPinterestVideo) {
    // since pinterest doesn't give us the video file itself or duration data,
    // simply leave the duration string out
    durationString = '';
  } else if (videoDuration) {
    durationString = formatTime(videoDuration);
  }
  return durationString;
}

export function normalizeMedia(media: Media | null | undefined) {
  const isVideo = isMediaVideo(media);
  const isCarousel = isMediaCarousel(media);
  const isLink = isMediaLink(media);
  const isReel = isMediaReel(media);
  const imageUrl = getMediaImageUrl(media);
  const postUrl = getMediaPostUrl(media);
  const videoDuration = getMediaVideoDuration(media);
  return {
    id: media?.id,
    isVideo,
    isCarousel,
    isLink,
    isReel,
    imageUrl,
    postUrl,
    videoDuration,
  };
}

export function checkTweetMediaIsOriginal(tweet: Media | null | undefined): boolean {
  let itemInsight: MediaPlatformData | null | undefined;
  if (tweet?.twitter) {
    itemInsight = tweet?.twitter;
  } else {
    itemInsight = tweet?.insights?.twitter || tweet?.insights;
  }
  return !(
    itemInsight?.inReplyToUserId ||
    itemInsight?.inReplyToStatusId ||
    itemInsight?.retweetedStatusId ||
    itemInsight?.quotedStatusId
  );
}

export function getFlatMediaThumbnailSrc(media: Media | null | undefined): string | null {
  return (
    media?.thumbnails?.smallSquare?.url ??
    media?.thumbnails?.small?.url ??
    media?.thumbnails?.mediumSquare?.url ??
    media?.sizes?.small?.url ??
    media?.sizes?.mediumSquare?.url ??
    media?.sizes?.original?.url ??
    media?.image?.sizes?.smallSquare?.url ??
    media?.image?.sizes?.small?.url ??
    media?.video?.thumbnails?.smallSquare?.url ??
    media?.video?.thumbnails?.small?.url ??
    null
  );
}

export function getFlatMediaType(
  media: Media | null | undefined,
): 'video' | 'text' | 'document' | 'audio' | 'image' {
  if (isMediaVideo(media)) return 'video';
  if (isMediaText(media)) return 'text';
  if (isMediaDocument(media)) return 'document';
  if (isMediaAudio(media)) return 'audio';
  return 'image';
}

export function getMetricValue(media: Media, metricAliasMap: Record<string, string>): unknown {
  const sourceData = getMediaSourceData(media);
  const channel = media.source === 'INSTAGRAM_STORY' ? media.source : media.source?.split('_')[0];
  const metric = metricAliasMap[channel ?? ''];
  return readDynamicKey(sourceData, metric ?? '');
}

/** Strip a demo `<brandId>_` prefix from a TikTok source id; bare (non-demo) ids pass through. */
export function stripDemoBrandIdPrefix(
  sourceId: string | number | null | undefined,
  brandId: number | null | undefined,
): string | null | undefined {
  if (sourceId == null) return sourceId;
  const id = String(sourceId);
  if (brandId == null) return id;
  const prefix = `${brandId}_`;
  return id.startsWith(prefix) ? id.slice(prefix.length) : id;
}
